import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stack,
  Button,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Icon,
  Flex,
  Divider,
  Image,
} from '@chakra-ui/react';
import { FaCheck, FaBook, FaArrowLeft, FaSearch, FaTags, FaFileExport, FaPlus, FaUpload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const FeatureBox = ({ title, description, icon }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      rounded="xl"
      shadow="md"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-3px)', shadow: 'lg' }}
    >
      <Flex
        w={12}
        h={12}
        align="center"
        justify="center"
        color="white"
        rounded="full"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={3}
      >
        {icon}
      </Flex>
      <Heading size="md" mb={2}>
        {title}
      </Heading>
      <Text color="gray.600" fontSize="sm">
        {description}
      </Text>
    </Stack>
  );
};

const ProblemBankProduct = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const seo = seoData.problemBank[language] || seoData.problemBank.en;
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const translations = {
    en: {
      backButton: 'Back to Products',
      heroTitle: 'Problem Bank Manager',
      heroDescription:
        'Organize and manage your question banks with powerful search, tagging, and categorization tools. Build a comprehensive library that grows with your needs.',
      heroButton: 'Get Started',
      featuresHeading: 'Powerful Management Features',
      featuresDescription: 'Everything you need to organize, search, and manage your problem library',
      features: [
        {
          title: 'Unlimited Storage',
          description: 'Store unlimited problems with no restrictions on quantity or file size. Scale as your library grows.',
          icon: FaBook,
        },
        {
          title: 'Advanced Search',
          description: 'Find exactly what you need with powerful search filters by topic, difficulty, type, and custom tags.',
          icon: FaSearch,
        },
        {
          title: 'Smart Categorization',
          description: 'Organize problems with custom categories, tags, and hierarchical folder structures.',
          icon: FaTags,
        },
        {
          title: 'Version Control',
          description: 'Track changes over time with built-in version history and rollback capabilities.',
          icon: FaFileExport,
        },
      ],
      benefitsHeading: 'Why Choose Problem Bank Manager?',
      benefitsDescription:
        'Stop wasting time searching through scattered files. Our Problem Bank Manager centralizes your entire question library in one powerful, searchable platform.',
      highlightTitle: 'Organize Everything',
      benefitsList: [
        'Centralized problem repository',
        'Collaborative sharing and permissions',
        'Import from multiple file formats',
        'Export to PDF, Word, and more',
        'Duplicate detection and management',
        'Bulk editing and operations',
        'Custom metadata fields',
        'API access for integrations',
      ],
      instructorSection: {
        title: 'Complete Instructor Control',
        description:
          'Powerful tools designed for educators to manage, create, and organize problem sets with maximum efficiency and flexibility.',
        cards: [
          {
            icon: FaSearch,
            title: 'View, Edit & Search Problems',
            description:
              'Access your entire problem library in one place. Search, filter, and edit problems with powerful tools designed for efficiency.',
            bullets: [
              'Browse all problems with advanced filters',
              'Search by keywords, topics, or difficulty',
              'Edit problems directly in the interface',
            ],
            imageAlt: 'View and Edit Problem Bank',
            imageSrc: '/screenshots/problem-bank.png',
            imageOnLeft: true,
          },
          {
            icon: FaPlus,
            title: 'Create Custom Problem Sets',
            description:
              'Build new problem sets by combining problems from existing sets or selecting directly from your problem bank. Mix and match to create the perfect assignment.',
            bullets: [
              'Combine problems from multiple sources',
              'Drag-and-drop problem selection',
              'Preview and adjust before publishing',
            ],
            imageAlt: 'Create Problem Sets',
            imageSrc: '/screenshots/problem-bank-create-pset.png',
            imageOnLeft: false,
          },
          {
            icon: FaUpload,
            title: 'Upload Your Own Content',
            description:
              'Already have problem sets in PDF format? Upload them directly to your problem bank and make them instantly searchable and editable.',
            bullets: [
              'Upload PDF problem sets with ease',
              'Automatic text extraction and indexing',
              'Integrate with existing problem library',
            ],
            imageAlt: 'Upload Problem Set PDFs',
            imageSrc: '/screenshots/problem-bank-upload-pset.png',
            imageOnLeft: true,
          },
        ],
      },
      highlightSection: {
        title: 'Your Problems, Your Way',
        description:
          "Whether you're curating problems from our library, creating custom sets, or uploading your own content, our Problem Bank Manager adapts to your workflow and makes content management effortless.",
      },
      cta: {
        title: 'Ready to Organize Your Problem Library?',
        description:
          'Join educators worldwide who trust our Problem Bank Manager to organize and streamline their content.',
        primary: 'Contact Sales',
        secondary: 'View All Products',
      },
    },
    ko: {
      backButton: '제품 목록으로',
      heroTitle: '문제은행 매니저',
      heroDescription:
        '강력한 검색, 태그, 분류 기능으로 문제은행을 체계적으로 관리하세요. 시간이 지날수록 성장하는 완벽한 라이브러리를 구축할 수 있습니다.',
      heroButton: '도입 상담하기',
      featuresHeading: '강력한 관리 기능',
      featuresDescription: '문제은행을 정리하고 검색하고 관리하는 데 필요한 모든 기능을 제공합니다.',
      features: [
        {
          title: '무제한 저장 공간',
          description: '문제 수나 파일 크기 제한 없이 원하는 만큼 저장하고 필요에 따라 유연하게 확장하세요.',
          icon: FaBook,
        },
        {
          title: '고급 검색 기능',
          description: '주제, 난이도, 유형, 사용자 지정 태그 등 다양한 필터로 원하는 문제를 빠르게 찾아보세요.',
          icon: FaSearch,
        },
        {
          title: '스마트 분류 체계',
          description: '사용자 정의 카테고리와 태그, 계층형 폴더 구조로 문제를 깔끔하게 정리합니다.',
          icon: FaTags,
        },
        {
          title: '버전 관리',
          description: '문제 수정 이력을 추적하고 필요할 때 이전 버전으로 간편하게 되돌릴 수 있습니다.',
          icon: FaFileExport,
        },
      ],
      benefitsHeading: '문제은행 매니저가 필요한 이유',
      benefitsDescription:
        '흩어져 있는 파일을 찾느라 시간을 낭비하지 마세요. 하나의 강력한 검색형 플랫폼에서 모든 문제를 관리하세요.',
      highlightTitle: '모든 문제를 한눈에',
      benefitsList: [
        '문제를 한곳에 모아 관리',
        '권한 기반 협업과 공유',
        '다양한 형식의 파일 가져오기',
        'PDF, Word 등 여러 형식으로 내보내기',
        '중복 문항 자동 감지',
        '대량 편집 및 일괄 작업 지원',
        '맞춤형 메타데이터 필드',
        '외부 서비스 연동을 위한 API 제공',
      ],
      instructorSection: {
        title: '교사를 위한 토털 솔루션',
        description:
          '교사가 문제를 관리하고, 새 문제집을 만들고, 자료를 정리하는 과정을 효율적으로 지원합니다.',
        cards: [
          {
            icon: FaSearch,
            title: '문제 조회·편집·검색',
            description:
              '모든 문제를 한 화면에서 조회하고 필터링하며 빠르게 수정할 수 있습니다.',
            bullets: [
              '고급 필터로 전체 문제 탐색',
              '키워드, 주제, 난이도로 즉시 검색',
              '인터페이스에서 바로 문제 편집',
            ],
            imageAlt: '문제 관리 화면',
            imageSrc: '/screenshots/problem-bank.png',
            imageOnLeft: true,
          },
          {
            icon: FaPlus,
            title: '맞춤형 문제집 생성',
            description:
              '기존 문제집과 문제은행의 문항을 조합해 원하는 구성의 문제집을 손쉽게 만들 수 있습니다.',
            bullets: [
              '여러 출처의 문제를 한 번에 조합',
              '드래그 앤 드롭으로 직관적인 구성',
              '발행 전에 미리보기로 최종 점검',
            ],
            imageAlt: '문제집 생성 화면',
            imageSrc: '/screenshots/problem-bank-create-pset.png',
            imageOnLeft: false,
          },
          {
            icon: FaUpload,
            title: '나만의 자료 업로드',
            description:
              '보유하고 있는 PDF 문제집을 업로드하면 자동으로 텍스트를 추출하고 검색할 수 있도록 변환합니다.',
            bullets: [
              'PDF 문제집을 간편하게 업로드',
              'AI가 텍스트를 추출하고 색인화',
              '기존 문제은행과 자연스럽게 통합',
            ],
            imageAlt: '문제집 업로드 화면',
            imageSrc: '/screenshots/problem-bank-upload-pset.png',
            imageOnLeft: true,
          },
        ],
      },
      highlightSection: {
        title: '나만의 문제은행 구축',
        description:
          '라이브러리에서 문제를 큐레이션하고, 맞춤형 문제집을 만들고, 직접 제작한 자료까지 통합해 완벽한 문제 관리 환경을 구현하세요.',
      },
      cta: {
        title: '지금 문제은행을 혁신하세요',
        description:
          '전 세계 교육자들이 문제은행 매니저로 콘텐츠를 효율적으로 관리하고 있습니다.',
        primary: '영업팀에 문의하기',
        secondary: '전체 제품 보기',
      },
    },
  };

  const t = translations[language] || translations.en;

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/products/problem-bank"
        lang={language}
        ogImage="/screenshots/problem-bank.png"
      />
      <Box>
        {/* Back Button */}
        <Box bg={useColorModeValue('white', 'gray.800')} py={4} borderBottom="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
        <Container maxW="container.xl">
          <Button
            leftIcon={<FaArrowLeft />}
            variant="ghost"
            onClick={() => navigate('/products')}
            _hover={{ color: '#FF6A5F' }}
          >
            {t.backButton}
          </Button>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-br, #FF6A5F, #A63FF5, #C56CFF)"
        color="white"
        py={20}
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Flex
              w={20}
              h={20}
              align="center"
              justify="center"
              color="white"
              rounded="full"
              bg="whiteAlpha.200"
            >
              <Icon as={FaBook} w={10} h={10} />
            </Flex>
            <Heading size="3xl" fontWeight="extrabold">
              {t.heroTitle}
            </Heading>
            <Text fontSize="xl" maxW="800px">
              {t.heroDescription}
            </Text>
            <Button
              size="lg"
              bg="white"
              color="#A63FF5"
              px={8}
              _hover={{
                bg: 'gray.100',
                transform: 'translateY(-2px)',
                shadow: 'xl',
              }}
              transition="all 0.3s"
              onClick={() => navigate('/contact')}
            >
              {t.heroButton}
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.featuresHeading}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                {t.featuresDescription}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {t.features.map((feature, index) => (
                <FeatureBox
                  key={index}
                  title={feature.title}
                  description={feature.description}
                  icon={<Icon as={feature.icon} w={6} h={6} />}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* Benefits Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <Box
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              h="400px"
              rounded="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              shadow="2xl"
            >
              <VStack spacing={4} color="white">
                <Icon as={FaBook} w={20} h={20} />
                <Heading size="xl">{t.highlightTitle}</Heading>
              </VStack>
            </Box>

            <VStack align="flex-start" spacing={6}>
              <Heading size="2xl">{t.benefitsHeading}</Heading>
              <Text fontSize="lg" color="gray.600">
                {t.benefitsDescription}
              </Text>
              <List spacing={3}>
                {t.benefitsList.map((benefit, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                    <Text>{benefit}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      <Divider />

      {/* Instructor Tools Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{t.instructorSection.title}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="800px">
                {t.instructorSection.description}
              </Text>
            </VStack>

            {t.instructorSection.cards.map((card, index) => (
              <SimpleGrid
                key={card.title}
                columns={{ base: 1, lg: 2 }}
                spacing={10}
                w="full"
                alignItems="center"
              >
                <Box order={{ base: 2, lg: card.imageOnLeft ? 1 : 2 }}>
                  <Image
                    src={card.imageSrc}
                    alt={card.imageAlt}
                    rounded="2xl"
                    shadow="2xl"
                    w="full"
                    h="auto"
                    objectFit="cover"
                    border="1px"
                    borderColor={borderColor}
                  />
                </Box>

                <VStack
                  align="flex-start"
                  spacing={4}
                  order={{ base: 1, lg: card.imageOnLeft ? 2 : 1 }}
                >
                  <Flex
                    w={14}
                    h={14}
                    align="center"
                    justify="center"
                    color="white"
                    rounded="full"
                    bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                  >
                    <Icon as={card.icon} w={6} h={6} />
                  </Flex>
                  <Heading size="xl">{card.title}</Heading>
                  <Text fontSize="lg" color="gray.600">
                    {card.description}
                  </Text>
                  <List spacing={3}>
                    {card.bullets.map((bullet, idx) => (
                      <ListItem key={idx} display="flex" alignItems="center">
                        <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                        <Text>{bullet}</Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </SimpleGrid>
            ))}

            {/* Key Feature Highlight */}
            <Box
              w="full"
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              p={10}
              rounded="2xl"
              shadow="2xl"
            >
              <VStack spacing={6} color="white">
                <Icon as={FaBook} w={16} h={16} />
                <Heading size="xl" textAlign="center">
                  {t.highlightSection.title}
                </Heading>
                <Text fontSize="lg" textAlign="center" maxW="700px">
                  {t.highlightSection.description}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* CTA Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <VStack
            spacing={6}
            bg={useColorModeValue('white', 'gray.800')}
            p={12}
            rounded="2xl"
            shadow="xl"
            textAlign="center"
          >
            <Heading size="2xl">{t.cta.title}</Heading>
            <Text fontSize="lg" color="gray.600" maxW="600px">
              {t.cta.description}
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                size="lg"
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                px={8}
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                  transform: 'translateY(-2px)',
                  shadow: 'xl',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/contact')}
              >
                {t.cta.primary}
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="#A63FF5"
                color="#A63FF5"
                px={8}
                _hover={{
                  bg: '#A63FF5',
                  color: 'white',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/products')}
              >
                {t.cta.secondary}
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default ProblemBankProduct;

